import {TypographyStylesProvider} from '@mantine/core';
import fileDownload from 'js-file-download';
import type {FC, ReactNode} from 'react';
import Markdown from 'react-markdown';
import type {ExtraProps} from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {MarkdownTableNode} from 'src/app/assistant/assistantCsv';
import {
  CSV_MIME_TYPE,
  buildMarkdownTableCsv,
  scoutCsvFileName,
} from 'src/app/assistant/assistantCsv';
import {TABLE_COLUMN_MIN_WIDTH} from 'src/app/assistant/Table/chatTableFormat';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Button} from 'src/design/atoms/Button';
import {Stack} from 'src/design/atoms/Stack';
import {stripMemoryMarkers} from 'src/shared/assistant/memoryMarkers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface AssistantMarkdownProps {
  content: string;
}

// Same-origin/relative links route in-app; external hrefs return undefined and
// render as plain text (layer-5 exfiltration defense).
const toInternalPath = (href: string | undefined): string | undefined => {
  if (!href || /\\|%5c/i.test(href)) return undefined;

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

const remarkPlugins = [remarkGfm];

const columnCount = (node: MarkdownTableNode | undefined): number => {
  if (!node) return 0;
  if (node.tagName === 'tr') {
    return (node.children ?? []).filter(
      cell => cell.tagName === 'th' || cell.tagName === 'td'
    ).length;
  }
  return Math.max(0, ...(node.children ?? []).map(columnCount));
};

// A model-written table scrolls sideways instead of widening the reply.
const MarkdownTable: FC<{node: ExtraProps['node']; children?: ReactNode}> = ({
  node,
  children,
}) => {
  const download = () => {
    const csv = node ? buildMarkdownTableCsv(node) : null;
    if (csv)
      fileDownload(csv, scoutCsvFileName('table', new Date()), CSV_MIME_TYPE);
  };
  return (
    <Stack gap={4} align="flex-start" mb="sm">
      <Box style={{overflowX: 'auto', maxWidth: '100%', alignSelf: 'stretch'}}>
        <table
          style={{
            minWidth: columnCount(node) * TABLE_COLUMN_MIN_WIDTH,
            margin: 0,
          }}>
          {children}
        </table>
      </Box>
      <Button
        size="compact-xs"
        variant="subtle"
        color="gray"
        aria-label="Download table as CSV"
        onClick={download}>
        Download CSV
      </Button>
    </Stack>
  );
};

// Hardened: no raw HTML (default react-markdown, no rehype-raw), images
// disabled, links limited to same-origin targets — external links become text.
export const AssistantMarkdown: FC<AssistantMarkdownProps> = ({content}) => {
  const navigate = useAppNavigate();

  return (
    <TypographyStylesProvider>
      <Markdown
        skipHtml
        remarkPlugins={remarkPlugins}
        disallowedElements={['img']}
        unwrapDisallowed
        components={{
          table: ({node, children}) => (
            <MarkdownTable node={node}>{children}</MarkdownTable>
          ),
          a: ({href, children}) => {
            const internalPath = toInternalPath(href);
            if (!internalPath) return <>{children}</>;

            return (
              <Anchor
                href={internalPath}
                onClick={event => {
                  event.preventDefault();
                  navigate(internalPath);
                }}>
                {children}
              </Anchor>
            );
          },
        }}>
        {stripMemoryMarkers(content)}
      </Markdown>
    </TypographyStylesProvider>
  );
};
